import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import VideoPage from './pages/VideoPage';
import ProductDetailPage from './pages/ProductDetailPage';
import AISecurityPage from './pages/AISecurityPage';
import FirePage from './pages/FirePage';
import AutomationPage from './pages/AutomationPage';
import InstallationPage from './pages/InstallationPage';
import ContactPage from './pages/ContactPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutPage from './pages/CheckoutPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import VisiotechImportPage from './pages/admin/VisiotechImportPage';
import SeoLandingPage from './pages/SeoLandingPage';
import { SEO_LANDING_SLUGS } from './data/seoLandings';
import BlogPage, { BlogPostPage } from './pages/BlogPage';
// Superior Pages (Pro)
import SuperiorAlarmG2Page from './pages/SuperiorAlarmG2Page';
import SuperiorAlarmG3Page from './pages/SuperiorAlarmG3Page';
import SuperiorVideoPage from './pages/SuperiorVideoPage';
import SuperiorSSIPage from './pages/SuperiorSSIPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
// Technology Pages (Video)
import TechVarifocalPage from './pages/TechVarifocalPage';
import TechEclairagePage from './pages/TechEclairagePage';
import TechIAPage from './pages/TechIAPage';
import TechAudioPage from './pages/TechAudioPage';
// SEO Local Pages
import AlarmeHeraultPage from './pages/AlarmeHeraultPage';
import AlarmeAudePage from './pages/AlarmeAudePage';
import AlarmeVarPage from './pages/AlarmeVarPage';
import AlarmeMarseillePage from './pages/AlarmeMarseillePage';
import AlarmeNimesPage from './pages/AlarmeNimesPage';
import AlarmeBéziersPage from './pages/AlarmeBéziersPage';
import AlarmePerpignanPage from './pages/AlarmePerpignanPage';
import AlarmeNarbonnePage from './pages/AlarmeNarbonnePage';
import AlarmeMontpellierPage from './pages/AlarmeMontpellierPage';
import './App.css';
import { Toaster } from './components/ui/sonner';

// ScrollToTop component to reset scroll position on navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Layout>
        <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/video" element={<VideoPage />} />
        <Route path="/product/:productId" element={<ProductDetailPage />} />
        <Route path="/ai-security" element={<AISecurityPage />} />
        <Route path="/fire" element={<FirePage />} />
        <Route path="/automation" element={<AutomationPage />} />
        <Route path="/installation" element={<InstallationPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/import-visiotech" element={<VisiotechImportPage />} />
        {/* SEO local usages — Landing pages générées depuis seoLandings.js */}
        {SEO_LANDING_SLUGS.map(slug => (
          <Route key={slug} path={`/${slug}`} element={<SeoLandingPage />} />
        ))}
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        {/* Blog Pages */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        {/* Superior Pages (Pro) */}
        <Route path="/superior-alarm-g2" element={<SuperiorAlarmG2Page />} />
        <Route path="/superior-alarm-g3" element={<SuperiorAlarmG3Page />} />
        <Route path="/superior-video" element={<SuperiorVideoPage />} />
        <Route path="/superior-ssi" element={<SuperiorSSIPage />} />
        {/* Technology Pages */}
        <Route path="/tech/varifocal" element={<TechVarifocalPage />} />
        <Route path="/tech/eclairage" element={<TechEclairagePage />} />
        <Route path="/tech/ia" element={<TechIAPage />} />
        <Route path="/tech/audio" element={<TechAudioPage />} />
        {/* SEO Local Pages */}
        <Route path="/alarme-ajax-herault" element={<AlarmeHeraultPage />} />
        <Route path="/alarme-ajax-aude" element={<AlarmeAudePage />} />
        <Route path="/alarme-ajax-var" element={<AlarmeVarPage />} />
        <Route path="/alarme-ajax-marseille" element={<AlarmeMarseillePage />} />
        <Route path="/alarme-ajax-nimes" element={<AlarmeNimesPage />} />
        <Route path="/alarme-ajax-beziers" element={<AlarmeBéziersPage />} />
        <Route path="/alarme-ajax-perpignan" element={<AlarmePerpignanPage />} />
        <Route path="/alarme-ajax-narbonne" element={<AlarmeNarbonnePage />} />
        <Route path="/alarme-montpellier" element={<AlarmeMontpellierPage />} />
        <Route path="/installation-montpellier" element={<InstallationPage />} />
      </Routes>
      </Layout>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Toaster />
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
